import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SearchIcon from '@mui/icons-material/Search';
import useApi from '../../hooks/use-api';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { useUser } from '../../providers/user-provider';
import { useToast } from '../../providers/toaster-provider';
import { usePermissions } from '../../providers/permissions-provider';
import DeletePopup from '../../components/popup/delete';
import { GLCode, SubGroup, SubHeading, MajorHeading, flattenCategories, resolveSubHeadingColor, hexToRgba } from '../../util/forecast-groups';

// Where to add newly-picked GL codes: directly under a Major Heading, or nested under one of its Sub Headings.
type PickerTarget = { kind: 'majorHeading' | 'subHeading'; id: number; name: string };

// GL code multi-select picker, used to add new GL codes to a Major Heading or
// a Sub Heading. Each selected code becomes its own Sub Group, named after
// the code itself. Codes already used anywhere else are disabled with a tooltip.
type GLCodePickerProps = {
    open: boolean;
    onClose: () => void;
    onSave: (categoryIds: number[]) => void;
    headingName: string;
    allCodes: GLCode[];
    linkedElsewhere: Record<number, string>;
};

const GLCodePicker: React.FC<GLCodePickerProps> = ({ open, onClose, onSave, headingName, allCodes, linkedElsewhere }) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            setSelectedIds([]);
            setSearch('');
        }
    }, [open]);

    const filtered = useMemo(() =>
        allCodes.filter(c =>
            c.name.toLowerCase().includes(search.toLowerCase()) ||
            c.code.toLowerCase().includes(search.toLowerCase())
        ),
        [allCodes, search]
    );

    const selectableFiltered = filtered.filter(c => !linkedElsewhere[c.id]);
    const allSelected = selectableFiltered.length > 0 && selectableFiltered.every(c => selectedIds.includes(c.id));

    const toggleOne = (id: number) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const toggleAll = () => {
        const selectableIds = selectableFiltered.map(c => c.id);
        if (allSelected) {
            setSelectedIds(prev => prev.filter(id => !selectableIds.includes(id)));
        } else {
            setSelectedIds(prev => Array.from(new Set([...prev, ...selectableIds])));
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Typography fontWeight={600} fontSize={16}>Add GL Codes to "{headingName}"</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 16, color: 'var(--color-text-muted)' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ mb: 1 }}
                />
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 0.75, borderBottom: '1px solid #eee', background: 'var(--color-container-background)', cursor: 'pointer' }}
                        onClick={toggleAll}
                    >
                        <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={!allSelected && selectableFiltered.some(c => selectedIds.includes(c.id))}
                            onChange={toggleAll}
                            onClick={e => e.stopPropagation()}
                            sx={{ p: 0.25, mr: 1, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' }, '&.MuiCheckbox-indeterminate': { color: 'var(--color-primary)' } }}
                        />
                        <Typography fontSize={13} fontWeight={600} color="var(--color-primary)">Select All</Typography>
                    </Box>
                    <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                        {filtered.map(code => {
                            const otherHeading = linkedElsewhere[code.id];
                            const isDisabled = !!otherHeading;
                            const isChecked = selectedIds.includes(code.id);
                            const row = (
                                <Box
                                    key={code.id}
                                    sx={{
                                        display: 'flex', alignItems: 'center', px: 1.5, py: 0.75,
                                        borderBottom: '1px solid #f5f5f5',
                                        cursor: isDisabled ? 'default' : 'pointer',
                                        opacity: isDisabled ? 0.5 : 1,
                                        '&:hover': { background: isDisabled ? 'transparent' : 'var(--color-container-background)' },
                                    }}
                                    onClick={() => !isDisabled && toggleOne(code.id)}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={isChecked}
                                        disabled={isDisabled}
                                        onChange={() => !isDisabled && toggleOne(code.id)}
                                        onClick={e => e.stopPropagation()}
                                        sx={{ p: 0.25, mr: 1, color: 'var(--color-primary)', '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                    />
                                    <Typography fontSize={13}>{code.name}</Typography>
                                </Box>
                            );
                            if (isDisabled) {
                                return (
                                    <Tooltip key={code.id} title={`Already added to ${otherHeading}`} placement="right" arrow>
                                        <span>{row}</span>
                                    </Tooltip>
                                );
                            }
                            return row;
                        })}
                        {filtered.length === 0 && (
                            <Typography fontSize={13} color="text.secondary" sx={{ px: 2, py: 2 }}>
                                No GL codes found
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                <Button onClick={onClose} variant="contained" sx={{ background: 'var(--color-container-background)', color: 'var(--color-default-text)', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={() => onSave(selectedIds)}
                    disabled={selectedIds.length === 0}
                    variant="contained"
                    sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, fontWeight: 600, boxShadow: 'none', textTransform: 'none' }}
                >
                    Add
                </Button>
            </DialogActions>
        </Dialog>
    );
};

type SubGroupRowProps = {
    label: string;
    onDragStart: () => void;
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnd: () => void;
    isDragging: boolean;
    isDragOver: boolean;
    onDelete: () => void;
    disabled?: boolean;
};

const SubGroupRow: React.FC<SubGroupRowProps> = ({ label, onDragStart, onDragOver, onDrop, onDragEnd, isDragging, isDragOver, onDelete, disabled }) => (
    <Box
        draggable={!disabled}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragEnd={onDragEnd}
        sx={{
            display: 'flex', alignItems: 'center', gap: 1, py: 0.75, px: 1,
            backgroundColor: isDragOver ? 'rgba(0, 182, 155, 0.1)' : 'transparent',
            opacity: isDragging ? 0.5 : 1,
            borderRadius: 1,
        }}
    >
        <DragIndicatorIcon fontSize="small" sx={{ color: 'var(--color-text-muted)', cursor: disabled ? 'default' : 'grab', opacity: disabled ? 0.4 : 1 }} />
        <Typography fontSize={14} sx={{ flex: 1, opacity: disabled ? 0.5 : 1 }}>{label}</Typography>
        <IconButton size="small" onClick={onDelete} disabled={disabled}>
            <DeleteOutlineIcon fontSize="small" sx={{ color: disabled ? 'var(--color-text-muted)' : '#F44336', opacity: disabled ? 0.5 : 1 }} />
        </IconButton>
    </Box>
);

// Rename + delete + "Add GL Codes" header row, shared by Major Headings and Sub Headings.
type RenameableHeaderProps = {
    name: string;
    onRename: (name: string) => void;
    onDelete: () => void;
    onAddCodes: () => void;
    extraAction?: React.ReactNode;
    bold?: boolean;
    color?: string;
    onColorChange?: (color: string) => void;
    disabled?: boolean;
};

const RenameableHeader: React.FC<RenameableHeaderProps> = ({ name: initialName, onRename, onDelete, onAddCodes, extraAction, bold = true, color, onColorChange, disabled }) => {
    const [name, setName] = useState(initialName);
    useEffect(() => setName(initialName), [initialName]);

    // Wraps on narrow screens: the name keeps a usable width and the buttons
    // move to the next line - on a phone they squeezed the name to an empty sliver.
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1, rowGap: 0.5, mb: 1 }}>
            <DragIndicatorIcon fontSize="small" sx={{ color: 'var(--color-text-muted)', cursor: disabled ? 'default' : 'grab', opacity: disabled ? 0.4 : 1 }} />
            {color && onColorChange && (
                <Tooltip title="Sub Heading color">
                    <Box
                        component="label"
                        sx={{
                            width: 20, height: 20, borderRadius: '50%', backgroundColor: color,
                            border: '1px solid rgba(0,0,0,0.15)', cursor: disabled ? 'default' : 'pointer', flexShrink: 0, position: 'relative', display: 'inline-block',
                            opacity: disabled ? 0.5 : 1,
                        }}
                    >
                        <input
                            type="color"
                            value={color}
                            disabled={disabled}
                            onChange={e => onColorChange(e.target.value)}
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: disabled ? 'default' : 'pointer', border: 0, padding: 0 }}
                        />
                    </Box>
                </Tooltip>
            )}
            <TextField
                size="small"
                value={name}
                disabled={disabled}
                onChange={e => setName(e.target.value)}
                onBlur={() => { if (name.trim() && name.trim() !== initialName) onRename(name.trim()); }}
                sx={{ flex: '1 1 180px', minWidth: 160, maxWidth: 320, '& .MuiInputBase-input': { fontWeight: bold ? 600 : 400 } }}
            />
            <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={onAddCodes} disabled={disabled} sx={{ textTransform: 'none', whiteSpace: 'nowrap', color: disabled ? 'var(--color-text-muted)' : 'var(--color-primary)', fontWeight: 600, opacity: disabled ? 0.6 : 1 }}>
                Add GL Codes
            </Button>
            {extraAction}
            <IconButton size="small" onClick={onDelete} disabled={disabled}>
                <DeleteOutlineIcon fontSize="small" sx={{ color: disabled ? 'var(--color-text-muted)' : '#F44336', opacity: disabled ? 0.5 : 1 }} />
            </IconButton>
        </Box>
    );
};

const GroupDefinition: React.FC = () => {
    const { user } = useUser();
    const { showSuccess, showError } = useToast();
    const { can } = usePermissions();
    const isReadOnly = !can('summaries_assumptions', 'update');
    const entityId = user?.entityId;

    const [majorHeadings, setMajorHeadings] = useState<MajorHeading[]>([]);
    const [allCodes, setAllCodes] = useState<GLCode[]>([]);
    const [reloadKey, setReloadKey] = useState(0);

    const [fetchGroups, setFetchGroups] = useApi();
    const [fetchCategories, setFetchCategories] = useApi();
    const [createHeading, setCreateHeading] = useApi();
    const [updateHeading, setUpdateHeading] = useApi();
    const [deleteHeading, setDeleteHeading] = useApi();
    const [createSubHeading, setCreateSubHeading] = useApi();
    const [updateSubHeadingApi, setUpdateSubHeadingApi] = useApi();
    const [deleteSubHeadingApi, setDeleteSubHeadingApi] = useApi();
    const [addSubGroups, setAddSubGroups] = useApi();
    const [addSubGroupsToSubHeadingApi, setAddSubGroupsToSubHeadingApi] = useApi();
    const [updateSubGroup, setUpdateSubGroup] = useApi();
    const [deleteSubGroup, setDeleteSubGroup] = useApi();

    const [deleteTarget, setDeleteTarget] = useState<{ type: 'heading' | 'subHeading' | 'subGroup'; id: number; label: string } | null>(null);
    const [pickerTarget, setPickerTarget] = useState<PickerTarget | null>(null);

    const [draggedHeadingIndex, setDraggedHeadingIndex] = useState<number | null>(null);
    const [dragOverHeadingIndex, setDragOverHeadingIndex] = useState<number | null>(null);
    const [draggedSubHeading, setDraggedSubHeading] = useState<{ majorHeadingId: number; index: number } | null>(null);
    const [dragOverSubHeading, setDragOverSubHeading] = useState<{ majorHeadingId: number; index: number } | null>(null);
    // Sub Group rows live in one of many lists (directly under a Major Heading, or under a Sub Heading) -
    // parentKey identifies which list a drag is happening within, e.g. "mh-3" or "sh-12".
    const [draggedSubGroup, setDraggedSubGroup] = useState<{ parentKey: string; index: number } | null>(null);
    const [dragOverSubGroup, setDragOverSubGroup] = useState<{ parentKey: string; index: number } | null>(null);

    const codeName = (categoryId: number) => allCodes.find(c => c.id === categoryId)?.name || `GL Code #${categoryId}`;

    useEffect(() => {
        document.title = "FineAnts | Assumptions";
        if (!entityId) return;
        const groupsReq = buildApiRequestConfig(routes.forecastGroups.fetchByEntity, [{ param: 'entityId', value: entityId }]);
        setFetchGroups(groupsReq);
        const catsReq = buildApiRequestConfig(routes.categories.fetchByEntity, [{ param: 'entityId', value: entityId }]);
        setFetchCategories(catsReq);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, reloadKey]);

    useEffect(() => {
        const { data, isLoading } = fetchGroups;
        if (data && !isLoading) setMajorHeadings(data.majorHeadings || []);
    }, [fetchGroups]);

    useEffect(() => {
        const { data, isLoading } = fetchCategories;
        if (data && !isLoading) setAllCodes(flattenCategories(data));
    }, [fetchCategories]);

    const reload = () => setReloadKey(k => k + 1);

    useEffect(() => {
        [
            createHeading, updateHeading, deleteHeading,
            createSubHeading, updateSubHeadingApi, deleteSubHeadingApi,
            addSubGroups, addSubGroupsToSubHeadingApi, updateSubGroup, deleteSubGroup,
        ].forEach(result => {
            if (result.data && !result.isLoading) reload();
            if (result.error) showError(typeof result.error === 'string' ? result.error : 'Something went wrong. Please try again.');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createHeading, updateHeading, deleteHeading, createSubHeading, updateSubHeadingApi, deleteSubHeadingApi, addSubGroups, addSubGroupsToSubHeadingApi, updateSubGroup, deleteSubGroup]);

    // Every GL code currently used anywhere (entity-wide), mapped to a display label for where it's linked
    const linkedElsewhere = useMemo(() => {
        const map: Record<number, string> = {};
        majorHeadings.forEach(mh => {
            mh.subGroups.forEach(sg => { map[sg.categoryId] = mh.name; });
            mh.subHeadings.forEach(sh => {
                sh.subGroups.forEach(sg => { map[sg.categoryId] = `${mh.name} → ${sh.name}`; });
            });
        });
        return map;
    }, [majorHeadings]);

    const handleAddHeading = () => {
        const { url, method } = buildApiRequestConfig(routes.forecastGroups.createMajorHeading);
        setCreateHeading({ url, method, body: { entityId, name: 'New Major Heading' } });
    };

    const handleRenameHeading = (id: number, name: string) => {
        const { url, method } = buildApiRequestConfig(routes.forecastGroups.updateMajorHeading, [{ param: 'id', value: id }]);
        setUpdateHeading({ url, method, body: { name } });
    };

    const handleAddSubHeading = (majorHeadingId: number) => {
        const { url, method } = buildApiRequestConfig(routes.forecastGroups.createSubHeading, [{ param: 'majorHeadingId', value: majorHeadingId }]);
        setCreateSubHeading({ url, method, body: { name: 'New Sub Heading' } });
    };

    const handleRenameSubHeading = (id: number, name: string) => {
        const { url, method } = buildApiRequestConfig(routes.forecastGroups.updateSubHeading, [{ param: 'id', value: id }]);
        setUpdateSubHeadingApi({ url, method, body: { name } });
    };

    const handleChangeSubHeadingColor = (id: number, color: string) => {
        const { url, method } = buildApiRequestConfig(routes.forecastGroups.updateSubHeading, [{ param: 'id', value: id }]);
        setUpdateSubHeadingApi({ url, method, body: { color } });
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        if (deleteTarget.type === 'heading') {
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.deleteMajorHeading, [{ param: 'id', value: deleteTarget.id }]);
            setDeleteHeading({ url, method });
        } else if (deleteTarget.type === 'subHeading') {
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.deleteSubHeading, [{ param: 'id', value: deleteTarget.id }]);
            setDeleteSubHeadingApi({ url, method });
        } else {
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.deleteSubGroup, [{ param: 'id', value: deleteTarget.id }]);
            setDeleteSubGroup({ url, method });
        }
        setDeleteTarget(null);
    };

    const handleAddCodes = (categoryIds: number[]) => {
        if (!pickerTarget) return;
        if (pickerTarget.kind === 'majorHeading') {
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.addSubGroups, [{ param: 'majorHeadingId', value: pickerTarget.id }]);
            setAddSubGroups({ url, method, body: { categoryIds } });
        } else {
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.addSubGroupsToSubHeading, [{ param: 'subHeadingId', value: pickerTarget.id }]);
            setAddSubGroupsToSubHeadingApi({ url, method, body: { categoryIds } });
        }
        setPickerTarget(null);
        showSuccess('GL codes added');
    };

    // Major Heading drag-and-drop (native HTML5, matching the row-drag pattern in table-virtual.tsx)
    const handleHeadingDrop = (targetIndex: number) => {
        if (draggedHeadingIndex !== null && draggedHeadingIndex !== targetIndex) {
            const dragged = majorHeadings[draggedHeadingIndex];
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.updateMajorHeading, [{ param: 'id', value: dragged.id }]);
            setUpdateHeading({ url, method, body: { orderNumber: targetIndex + 1 } });
        }
        setDraggedHeadingIndex(null);
        setDragOverHeadingIndex(null);
    };

    // Sub Heading drag-and-drop - reorders among siblings within the same Major Heading
    const handleSubHeadingDrop = (majorHeadingId: number, subHeadings: SubHeading[], targetIndex: number) => {
        if (draggedSubHeading && draggedSubHeading.majorHeadingId === majorHeadingId && draggedSubHeading.index !== targetIndex) {
            const dragged = subHeadings[draggedSubHeading.index];
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.updateSubHeading, [{ param: 'id', value: dragged.id }]);
            setUpdateSubHeadingApi({ url, method, body: { orderNumber: targetIndex + 1 } });
        }
        setDraggedSubHeading(null);
        setDragOverSubHeading(null);
    };

    // Sub Group drag-and-drop - reorders among siblings within the same list (parentKey)
    const handleSubGroupDrop = (parentKey: string, subGroups: SubGroup[], targetIndex: number) => {
        if (draggedSubGroup && draggedSubGroup.parentKey === parentKey && draggedSubGroup.index !== targetIndex) {
            const dragged = subGroups[draggedSubGroup.index];
            const { url, method } = buildApiRequestConfig(routes.forecastGroups.updateSubGroup, [{ param: 'id', value: dragged.id }]);
            setUpdateSubGroup({ url, method, body: { orderNumber: targetIndex + 1 } });
        }
        setDraggedSubGroup(null);
        setDragOverSubGroup(null);
    };

    return (
        <Box p={2}>
            <Typography color="text.secondary" mb={3}>
                These Headings and Groups will be used for the formatting of Forecasts and Forecasts vs Actuals.
            </Typography>

            <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={handleAddHeading}
                disabled={isReadOnly}
                sx={{
                    background: isReadOnly ? 'var(--color-text-muted)' : 'var(--color-primary)', color: '#fff', fontWeight: 600, textTransform: 'none', boxShadow: 'none', mb: 2, opacity: isReadOnly ? 0.5 : 1,
                    '&:hover': { background: isReadOnly ? 'var(--color-text-muted)' : 'var(--color-primary)' },
                }}
            >
                Add Major Heading
            </Button>

            {majorHeadings.map((mh, headingIndex) => {
                const directParentKey = `mh-${mh.id}`;
                return (
                <Box
                    key={mh.id}
                    draggable={!isReadOnly}
                    onDragStart={() => setDraggedHeadingIndex(headingIndex)}
                    onDragOver={(e) => { e.preventDefault(); setDragOverHeadingIndex(headingIndex); }}
                    onDrop={() => handleHeadingDrop(headingIndex)}
                    onDragEnd={() => { setDraggedHeadingIndex(null); setDragOverHeadingIndex(null); }}
                    sx={{
                        border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 2,
                        backgroundColor: dragOverHeadingIndex === headingIndex ? 'rgba(0, 182, 155, 0.05)' : 'var(--color-page-background)',
                        opacity: draggedHeadingIndex === headingIndex ? 0.5 : 1,
                    }}
                >
                    <RenameableHeader
                        name={mh.name}
                        onRename={(name) => handleRenameHeading(mh.id, name)}
                        onDelete={() => setDeleteTarget({ type: 'heading', id: mh.id, label: mh.name })}
                        onAddCodes={() => setPickerTarget({ kind: 'majorHeading', id: mh.id, name: mh.name })}
                        disabled={isReadOnly}
                        extraAction={
                            <Button
                                size="small"
                                startIcon={<PlaylistAddIcon fontSize="small" />}
                                onClick={() => handleAddSubHeading(mh.id)}
                                disabled={isReadOnly}
                                sx={{ textTransform: 'none', whiteSpace: 'nowrap', color: 'var(--color-text-muted)', fontWeight: 600, opacity: isReadOnly ? 0.5 : 1 }}
                            >
                                Add Sub Heading
                            </Button>
                        }
                    />

                    <Box sx={{ pl: 3 }}>
                        {mh.subGroups.map((sg, sgIndex) => (
                            <SubGroupRow
                                key={sg.id}
                                label={codeName(sg.categoryId)}
                                onDragStart={() => setDraggedSubGroup({ parentKey: directParentKey, index: sgIndex })}
                                onDragOver={(e) => { e.preventDefault(); setDragOverSubGroup({ parentKey: directParentKey, index: sgIndex }); }}
                                onDrop={() => handleSubGroupDrop(directParentKey, mh.subGroups, sgIndex)}
                                onDragEnd={() => { setDraggedSubGroup(null); setDragOverSubGroup(null); }}
                                isDragging={draggedSubGroup?.parentKey === directParentKey && draggedSubGroup?.index === sgIndex}
                                isDragOver={dragOverSubGroup?.parentKey === directParentKey && dragOverSubGroup?.index === sgIndex}
                                onDelete={() => setDeleteTarget({ type: 'subGroup', id: sg.id, label: codeName(sg.categoryId) })}
                                disabled={isReadOnly}
                            />
                        ))}
                        {mh.subGroups.length === 0 && mh.subHeadings.length === 0 && (
                            <Typography fontSize={13} color="text.secondary" sx={{ py: 1 }}>
                                No GL codes yet — this heading will show a $0 total until you add some.
                            </Typography>
                        )}

                        {mh.subHeadings.map((sh, shIndex) => {
                            const subParentKey = `sh-${sh.id}`;
                            const shColor = resolveSubHeadingColor(sh.color, shIndex);
                            return (
                                <Box
                                    key={sh.id}
                                    draggable={!isReadOnly}
                                    onDragStart={() => setDraggedSubHeading({ majorHeadingId: mh.id, index: shIndex })}
                                    onDragOver={(e) => { e.preventDefault(); setDragOverSubHeading({ majorHeadingId: mh.id, index: shIndex }); }}
                                    onDrop={() => handleSubHeadingDrop(mh.id, mh.subHeadings, shIndex)}
                                    onDragEnd={() => { setDraggedSubHeading(null); setDragOverSubHeading(null); }}
                                    sx={{
                                        border: `1px solid ${hexToRgba(shColor, 0.35)}`, borderLeft: `3px solid ${shColor}`, borderRadius: 1.5, p: 1.5, mt: 1.5, ml: 1.5,
                                        backgroundColor: dragOverSubHeading?.majorHeadingId === mh.id && dragOverSubHeading?.index === shIndex
                                            ? hexToRgba(shColor, 0.18) : hexToRgba(shColor, 0.06),
                                        opacity: draggedSubHeading?.majorHeadingId === mh.id && draggedSubHeading?.index === shIndex ? 0.5 : 1,
                                    }}
                                >
                                    <RenameableHeader
                                        name={sh.name}
                                        onRename={(name) => handleRenameSubHeading(sh.id, name)}
                                        onDelete={() => setDeleteTarget({ type: 'subHeading', id: sh.id, label: sh.name })}
                                        onAddCodes={() => setPickerTarget({ kind: 'subHeading', id: sh.id, name: sh.name })}
                                        color={shColor}
                                        onColorChange={(color) => handleChangeSubHeadingColor(sh.id, color)}
                                        disabled={isReadOnly}
                                    />
                                    <Box sx={{ pl: 4, borderLeft: `2px dashed ${hexToRgba(shColor, 0.35)}`, ml: 1 }}>
                                        {sh.subGroups.map((sg, sgIndex) => (
                                            <SubGroupRow
                                                key={sg.id}
                                                label={codeName(sg.categoryId)}
                                                onDragStart={() => setDraggedSubGroup({ parentKey: subParentKey, index: sgIndex })}
                                                onDragOver={(e) => { e.preventDefault(); setDragOverSubGroup({ parentKey: subParentKey, index: sgIndex }); }}
                                                onDrop={() => handleSubGroupDrop(subParentKey, sh.subGroups, sgIndex)}
                                                onDragEnd={() => { setDraggedSubGroup(null); setDragOverSubGroup(null); }}
                                                isDragging={draggedSubGroup?.parentKey === subParentKey && draggedSubGroup?.index === sgIndex}
                                                isDragOver={dragOverSubGroup?.parentKey === subParentKey && dragOverSubGroup?.index === sgIndex}
                                                onDelete={() => setDeleteTarget({ type: 'subGroup', id: sg.id, label: codeName(sg.categoryId) })}
                                                disabled={isReadOnly}
                                            />
                                        ))}
                                        {sh.subGroups.length === 0 && (
                                            <Typography fontSize={13} color="text.secondary" sx={{ py: 1 }}>
                                                No GL codes yet.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
                );
            })}

            {majorHeadings.length === 0 && (
                <Typography color="text.secondary">
                    No Major Headings yet. Click "Add Major Heading" to get started.
                </Typography>
            )}

            {pickerTarget && (
                <GLCodePicker
                    open={!!pickerTarget}
                    onClose={() => setPickerTarget(null)}
                    onSave={handleAddCodes}
                    headingName={pickerTarget.name}
                    allCodes={allCodes}
                    linkedElsewhere={linkedElsewhere}
                />
            )}

            <DeletePopup
                open={!!deleteTarget}
                message={`Delete "${deleteTarget?.label}"?`}
                description={
                    deleteTarget?.type === 'heading'
                        ? 'This will also remove its Sub Headings and GL codes. They will become unassigned again.'
                        : deleteTarget?.type === 'subHeading'
                            ? 'This will also remove its GL codes from this Sub Heading. They will become unassigned again.'
                            : 'This GL code will become unassigned again.'
                }
                red
                confirmLabel="Delete"
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
        </Box>
    );
};

export default GroupDefinition;
